import { z } from 'zod';

import { chatUiBase } from '../../shared/schema/base.js';
import { CHAT_UI_MAX_LEGS } from '../../shared/schema/limits.js';
import { TravelerRefSchema } from '../../shared/schema/traveler_ref.js';
import { LegHeatSchema } from '../flight_cheaper_dates/flight_cheaper_dates_schema.js';

/** Janela pré-buscada do heatmap: `fromMonth` = 'YYYY-MM' do 1º mês; `count`
 *  meses consecutivos. O paging ‹ › é CLIENT-SIDE sobre esses meses (sem
 *  round-trip); cap 3 segura o custo do provider (uma chamada SERP por mês). */
const PlannerWindowSchema = z.object({
  fromMonth: z.string().regex(/^\d{4}-(0[1-9]|1[0-2])$/),
  count: z.number().int().min(1).max(3),
});

/** Par de datas escolhido (ida + volta opcional) — um shape só evita drift entre
 *  o `cheapest` do bloco e o echo `search` da selection. */
const dateChoiceShape = {
  outboundDate: z.iso.date(),
  returnDate: z.iso.date().optional(),
} as const;

const PassengersSchema = z.number().int().min(1).max(9);

/** Um trecho do modo multi-trecho: cada leg é um voo one-way independente
 *  (origem → destino numa data). `date` opcional no bloco (card-first: o
 *  cliente ainda pode estar preenchendo). O echo (selection `search_multi`)
 *  exige a data. `originLabel`/`destinationLabel` são só de display (espelham o
 *  `route.originLabel`/`destinationLabel` do bloco): o wire segue no IATA cru. */
const PlannerLegSchema = z.object({
  origin: z.string(),
  destination: z.string(),
  originLabel: z.string().optional(),
  destinationLabel: z.string().optional(),
  date: z.iso.date().optional(),
});
export type PlannerLeg = z.infer<typeof PlannerLegSchema>;

/** Modo da viagem — one-way/round-trip (ida + volta) ou multi-trecho (N legs). */
export const PlannerTripTypeSchema = z.enum(['one_way', 'round_trip', 'multi_leg']);
export type PlannerTripType = z.infer<typeof PlannerTripTypeSchema>;

// Cap the traveler set to the same 1..9 bound as `passengers` (they are the same
// people). Shared so the block + both selection variants stay in lockstep.
const TravelersSchema = z.array(TravelerRefSchema).min(1).max(9);

/**
 * Membro `flight_planner` do `ChatUiSchema` — o filtro pré-ofertas SEMPRE
 * exibido (substitui o gate condicional `flight_cheaper_dates` no agents-v2).
 * Grades/cheapest opcionais: ausentes = provider sem calendário (degrade D13,
 * card vira filtro puro). A grade é só cor (tier 1–5): o `price` por dia e a
 * `currency` existem para a economia estimada da saia (#5246), nunca para a célula.
 */
export const FlightPlannerBlockSchema = z
  .object({
    ...chatUiBase,
    kind: z.literal('flight_planner'),
    /** Marcador da interação. `search` congela o card (resolvedInteractionIds);
     *  `requery` reemite um card novo (challengeId novo) — nunca congela. */
    challengeId: z.string().min(1),
    route: z.object({
      // Vazio permitido: estado card-first (o card orienta o preenchimento da
      // rota desde o 1º turno). O echo (selection) continua exigindo min(1).
      origin: z.string(),
      destination: z.string(),
      originLabel: z.string().optional(),
      destinationLabel: z.string().optional(),
    }),
    /** Filtro atual (reflete os slots do FSM). Pax segue no payload de echo. */
    departDate: z.iso.date().optional(),
    returnDate: z.iso.date().optional(),
    passengers: PassengersSchema.optional(),
    /** Viajantes escolhidos no campo QUEM (Approach A: fonte única; passengers = length). */
    travelers: TravelersSchema.optional(),
    /** Qual viajante rege a política de compliance (default = requester). */
    policyTravelerId: z.string().min(1).optional(),
    /** Id do usuário logado — rotula a entry correspondente do QUEM como "Você". */
    requesterId: z.string().min(1).optional(),
    window: PlannerWindowSchema,
    outbound: LegHeatSchema.optional(),
    return: LegHeatSchema.optional(),
    cheapest: z.object(dateChoiceShape).optional(),
    /** Moeda (ISO 4217) dos `price` dos dias de `outbound`/`return`: a do mercado do
     *  workspace em que o calendário foi lido. Ausente = sem economia a mostrar. */
    currency: z
      .string()
      .regex(/^[A-Z]{3}$/)
      .optional(),
    /**
     * Heat por ROTA + JANELA, indexado por `'ORIGEM:DESTINO:AAAA-MM'` (ver
     * `routeHeatKey`) — a morada do calor do multi-trecho, onde `outbound`/`return`
     * (dois slots) não cabem N pernas.
     *
     * Endereçado por rota e não por índice de perna por duas razões: heat É de uma
     * rota, e as pernas do card não existem no bloco quando a pessoa entra no multi
     * a partir de um card binário — não há índice onde escrever, e o heat era
     * descartado em silêncio. Remover uma perna só deixa uma entrada sem uso. O mês
     * na chave é a janela que a resposta descreve: sem ele, uma resposta atrasada
     * sobrescrevia a janela vigente e pernas de mesma rota disputavam uma entrada só.
     *
     * Preenchido pelo SHELL a partir do `planner-preview` (READ), nunca pelo agente.
     * Aditivo e opcional: bloco antigo do histórico replaya sem cor, como hoje — e o
     * persistido com a chave só-rota do formato anterior também (nada o lê; o card
     * re-pede a janela visível).
     */
    routeHeat: z.record(z.string(), LegHeatSchema).optional(),
    /** Modo multi-trecho: `tripType` + `legs[]` (1..`CHAT_UI_MAX_LEGS`) + o leg em
     *  edição. Ausentes = bloco binário legado (ida + volta opcional). */
    tripType: PlannerTripTypeSchema.optional(),
    legs: z.array(PlannerLegSchema).min(1).max(CHAT_UI_MAX_LEGS).optional(),
    activeLegIndex: z.number().int().min(0).optional(),
  })
  .strict()
  // Refine SÓ direcional: um bloco multi_leg PRECISA de `legs`. Não rejeita o
  // binário legado (sem tripType) — só barra o modo multi declarado sem trechos.
  .refine((b) => b.tripType !== 'multi_leg' || b.legs != null, {
    message: 'multi_leg requires legs',
    path: ['legs'],
  });
export type FlightPlannerBlock = z.infer<typeof FlightPlannerBlockSchema>;

/**
 * Payload ecoado em `confirmation.selections` — discriminado por `action`:
 * `search` = rota + data(s) escolhida(s) → o map-guard semeia os slots e busca
 * (sintética `submit_planner_search`); `requery` = filtro editado → re-consulta
 * determinística sem LLM (sintética `submit_planner_requery`). O `action`
 * explícito é o que evita o mis-route no map-guard do flight (payloads legados,
 * sem `action`, caem no switch por fase). Ambos carregam origin/destination —
 * o FSM pode ainda não ter slots.
 *
 * Vocabulário deliberado: search carrega DATAS ESCOLHIDAS (outboundDate/returnDate,
 * espelha 'cheapest'); requery carrega o FILTRO (departDate/returnDate, espelha os
 * campos do bloco).
 */
export const FlightPlannerSelectionSchema = z.discriminatedUnion('action', [
  z
    .object({
      action: z.literal('search'),
      origin: z.string().min(1),
      destination: z.string().min(1),
      ...dateChoiceShape,
      passengers: PassengersSchema.optional(),
      travelers: TravelersSchema.optional(),
      policyTravelerId: z.string().min(1).optional(),
    })
    .strict(),
  z
    .object({
      action: z.literal('requery'),
      origin: z.string().min(1),
      destination: z.string().min(1),
      departDate: z.iso.date().optional(),
      returnDate: z.iso.date().optional(),
      passengers: PassengersSchema.optional(),
      travelers: TravelersSchema.optional(),
      policyTravelerId: z.string().min(1).optional(),
    })
    .strict(),
  // Multi-trecho: ecoa a lista completa de legs (cada um origem/destino/data
  // exigidos). `editedIndex` = qual leg o cliente acabou de editar (opcional).
  // O map-guard mapeia esta variante para a sintética `submit_multi_planner`.
  z
    .object({
      action: z.literal('search_multi'),
      tripType: z.literal('multi_leg'),
      legs: z
        .array(
          z.object({
            origin: z.string().min(1),
            destination: z.string().min(1),
            date: z.iso.date(),
          }),
        )
        .min(1)
        .max(CHAT_UI_MAX_LEGS),
      editedIndex: z.number().int().min(0).optional(),
      passengers: PassengersSchema.optional(),
      travelers: TravelersSchema.optional(),
      policyTravelerId: z.string().min(1).optional(),
    })
    .strict(),
]);
export type FlightPlannerSelection = z.infer<typeof FlightPlannerSelectionSchema>;
